import axios from 'axios';
import Pako from 'pako';
import base64 from 'base-64';

const CHUNK_SIZE = 1024;

/**
 * Check if data is valid
 */
const isValidElement = (data) => {
  return data !== null && data !== undefined && data !== '';
};

/**
 * Decompress API response using Pako
 */
export const decompressApiResponseSaga = async (data) => {
  // Check if data is valid
  if (!isValidElement(data)) {
    return null;
  }

  try {
    // Decode base64 data
    const binaryString = base64.decode(data);
    const binaryData = [];

    // Convert binary string to array of char codes
    for (let i = 0; i < binaryString.length; i++) {
      binaryData.push(binaryString.charCodeAt(i));
    }

    // Initialize Pako for inflation (decompression)
    const inflator = new Pako.Inflate({ to: 'string' });
    let index = 0;

    while (index < binaryData.length) {
      const chunk = binaryData.slice(index, index + CHUNK_SIZE);
      inflator.push(chunk, index + CHUNK_SIZE >= binaryData.length);
      
      // Small delay to allow other tasks to run
      await new Promise(resolve => setTimeout(resolve, 0));
      
      index += CHUNK_SIZE;
    }

    // Check if the inflation was successful
    if (inflator.err) {
      throw new Error(`Decompression error: ${inflator.err}`);
    }

    // Result after decompression
    const result = inflator.result;

    // Parse the decompressed result (assuming it's JSON)
    const finalData = JSON.parse(result);

    return finalData;
  } catch (error) {
    console.error('Error during decompression', error);
    return null;
  }
};

/**
 * Get menu from API
 */
export const getMenu = async (action) => {
  try {
    const menu = await axios.get(
      `https://foodhub-api.t2sonline.com/consumer/store/${action?.store_id}/menu/foodhub/all.json?v=${action?.syncMenu}?t=${Date.now()}`,
      {
        headers: {
          'accept': 'application/json, text/plain, */*',
          'api-token': action?.api_token,
          'store': action?.store_id,
          'locale': 'united kingdom',
          'language': 'en',
          'canceltoken': '[object Object]'
        }
      }
    );

    const decompressed = await decompressApiResponseSaga(menu.data.data[0]);
    return decompressed;
  } catch (error) {
    console.error('Error fetching menu:', error);
    throw error;
  }
};

/**
 * Get addons from API
 */
export const getAddons = async (action) => {
  try {
    const addons = await axios.get(
      `https://foodhub.co.uk/api/consumer/store/${action?.store_id}/addons/all.json`,
      {
        headers: {
          'api-token': action?.api_token
        }
      }
    );

    const decompressed = await decompressApiResponseSaga(addons.data.data[0]);
    return decompressed;
  } catch (error) {
    console.error('Error fetching addons:', error);
    throw error;
  }
};

/**
 * Restructure menu into hierarchical format
 * Structure: categories -> subcategories -> items
 */
export const restrucreMenu = async (menu, addons, menuVersion) => {
  try {
    const menuNew = [];

    if (menuVersion === 'v1') {
      menu.forEach(cat => {
        const subs = [];

        cat.subcat.forEach(sub => {
          const items = [];

          sub.item.forEach(item => {
            items.push({
              id: item?.id,
              name: item?.name.replace(/^[^.]*\./, '').trim(),
              description: item?.description,
              subcat: item?.subcat,
              price: item?.price,
              image: item?.image
            });
          });

          subs.push({
            id: sub?.id,
            name: sub?.name.replace(/^[^.]*\./, '').trim(),
            description: sub?.description,
            category: sub?.category,
            items: items
          });
        });

        menuNew.push({
          id: cat?.id,
          name: cat?.name,
          subs: subs
        });
      });

      return menuNew;
    } else {
      // v2 menu structure
      menu.forEach(cat => {
        const subs = [];

        cat.subcat.forEach(sub => {
          const items = [];

          sub.item.forEach(i => {
            const addon = addons[i?.item_addon_cat]?.addon;

            if (addon && addon[0]?.type === 'radio' && addon[1]?.type === 'radio') {
              // If the condition is met, treat the item as a subcategory
              const addonItems = addon.map(a => ({
                id: a?.id,
                name: a?.name.replace(/^[^.]*\./, '').trim(),
                subcat: i?.id,
                price: (parseFloat(a?.price) + parseFloat(i?.price)).toFixed(2)
              }));

              subs.push({
                id: i?.id,
                name: i?.name.replace(/^[^.]*\./, '').trim(), // Treat the item as a subcategory
                description: i?.description,
                price: i?.price,
                category: sub?.category,
                items: addonItems // Addons become the items of this subcategory
              });
            } else {
              // If the condition is NOT met, treat it as a normal item
              items.push({
                id: i?.id,
                name: i?.name.replace(/^[^.]*\./, '').trim(),
                description: i?.description,
                subcat: i?.id,
                price: i?.price,
                image: i?.image
              });
            }
          });

          if (items.length > 0) {
            subs.push({
              id: sub?.id,
              name: sub?.name.replace(/^[^.]*\./, '').trim(),
              description: sub?.description,
              category: sub?.category,
              items: items
            });
          }
        });

        menuNew.push({
          id: cat?.id,
          name: cat?.name,
          subs: subs
        });
      });

      return menuNew;
    }
  } catch (error) {
    console.error('Error restructuring menu:', error);
    throw error;
  }
};

